'use client'

import { addDays } from '@/components/coverage/CoverageSection'
import usePeriodView from '@/components/coverage/usePeriodView'
import {
  coverageLabel,
  kindLabel,
  totalOf,
  WING_BORDER,
  WING_TEXT,
  WINGS,
} from '@/components/feeds/TopicCoverage'
import SubscribeCard from '@/components/now/SubscribeCard'
import StorylineDayChart from '@/components/storylines/StorylineDayChart'
import TrackedLink from '@/components/tracking/TrackedLink'
import CoverageBlock from '@/components/ui/CoverageBlock'
import LeanMark from '@/components/ui/LeanMark'
import OutletIcon from '@/components/ui/OutletIcon'
import PeriodPills from '@/components/ui/PeriodPills'
import { TOPIC_PERIODS, TOPICS_VIEW_KEY } from '@/constants/topics'
import { track } from '@/lib/tracking'
import { getBiasCategory } from '@/utils/bias'
import Link from 'next/link'
import { useState } from 'react'

// A topic's page (/topic/<slug>): a person, organization or place the
// pipeline linked to Wikidata from story overviews (tasks/entities.py).
//
// The identity mocks' page (2026-11-01), top to bottom:
//   1. A centred masthead: "Topics · Person", the name, Wikipedia's
//      summary with its credit, and the period as a 7 / 30 / 90 days pill.
//   2. The coverage block: "Last 30 days · 16 stories · 726 articles · Pano
//      rates the coverage Balanced", each side's count over one bar.
//   3. Two columns: the stories ("Where she appears": date, title, the
//      overview's sentence that names the topic as a quote, the story's bar
//      with its counts and label), and a side column with the storylines
//      those stories belong to and "Who covers her most", the top three
//      outlets of each side.
// Everything under the pill follows it. The page opens on the shortest
// period with a story in it, or the one in the address (?period=month).

const STORIES_N = 10
const STORYLINES_N = 5
const OUTLETS_N = 3
const LINK =
  'underline decoration-foreground/60 decoration-[1.5px] underline-offset-4 transition-colors hover:text-foreground hover:decoration-foreground'
const SIDE_LABEL = { left: 'Left', center: 'Center', right: 'Right' }

const plural = (n, one, many) => `${n.toLocaleString()} ${n === 1 ? one : many}`

// The headings' pronoun. The data carries no gender (entities has a kind:
// person, organization, place), so a person is "they" and anything else
// "it"; nothing is guessed from a name.
function pronouns(kind) {
  return kind === 'person'
    ? { appears: 'Where they appear', covers: 'Who covers them most' }
    : { appears: 'Where it appears', covers: 'Who covers it most' }
}

export default function TopicProfile({
  entity,
  stories = [],
  storylines = [],
  days = [],
  outlets = {},
  today,
  isGuest = false,
}) {
  const fromOf = (id) =>
    addDays(today, -(TOPIC_PERIODS.find((p) => p.id === id).days - 1))
  // Opens on the past week, or the shortest period with a story in it.
  const [period, setPeriod] = usePeriodView({
    initial:
      TOPIC_PERIODS.find((p) => stories.some((s) => s.day >= fromOf(p.id)))
        ?.id || 'quarter',
    periods: TOPIC_PERIODS,
    listPath: '/topics',
    viewKey: TOPICS_VIEW_KEY,
  })
  const [allStories, setAllStories] = useState(false)
  const [allStorylines, setAllStorylines] = useState(false)

  const from = fromOf(period)
  const periodDays = TOPIC_PERIODS.find((p) => p.id === period).days
  const inPeriod = stories.filter((s) => s.day >= from)
  // The period's articles by side, each article once (the days count them
  // that way), for the coverage block.
  const lean = { left: 0, center: 0, right: 0 }
  for (const d of days)
    if (d.day >= from) for (const w of WINGS) lean[w] += d.wings?.[w] || 0
  const articles = totalOf(lean)
  // The chart's days: the period's days that had coverage, oldest first.
  const chartDays = days
    .filter((d) => d.day >= from && totalOf(d.wings) > 0)
    .sort((a, b) => a.day.localeCompare(b.day))
  const label = coverageLabel(lean)

  const shownStories = allStories ? inPeriod : inPeriod.slice(0, STORIES_N)
  // The storylines this period's stories belong to, by how many of them
  // each holds.
  const sagaById = new Map(storylines.map((s) => [s.id, s]))
  const sagaCounts = new Map()
  for (const s of inPeriod)
    for (const id of s.storyline_ids || [])
      sagaCounts.set(id, (sagaCounts.get(id) || 0) + 1)
  const periodStorylines = [...sagaCounts.entries()]
    .filter(([id]) => sagaById.has(id))
    .map(([id, chapters]) => ({ ...sagaById.get(id), chapters }))
    .sort((a, b) => b.chapters - a.chapters)
  const shownStorylines = allStorylines
    ? periodStorylines
    : periodStorylines.slice(0, STORYLINES_N)
  // The period's top outlets by side: the three with the most articles on
  // each wing.
  const bySide = { left: [], center: [], right: [] }
  for (const o of outlets[period] || []) {
    const wing = typeof o.bias === 'number' ? getBiasCategory(o.bias) : 'center'
    if (bySide[wing].length < OUTLETS_N) bySide[wing].push(o)
  }
  const anyOutlets = WINGS.some((w) => bySide[w].length > 0)
  const words = pronouns(entity.kind)

  return (
    <article className="page-wide pb-16 pt-8 sm:pt-10">
      {/* No back link or Share row: the mocks have none, as on a storyline
          page. The dateline's "Topics" is the way back. */}
      <header className="text-center">
        <p className="dateline">
          <Link href="/topics" className="underline">
            Topics
          </Link>
          {entity.kind && <> · {kindLabel(entity.kind)}</>}
        </p>
        <h1 className="my-3 font-serif text-[34px] font-medium leading-[1.05] sm:text-[60px] sm:leading-[1.02]">
          {entity.name}
        </h1>
        {entity.summary_short && (
          <>
            <p className="mx-auto max-w-[680px] font-serif text-[17px] leading-[1.55] text-secondary-foreground sm:text-lg">
              {entity.summary_short}
            </p>
            <p className="mt-1.5 font-sans text-xs text-muted-foreground">
              From{' '}
              <TrackedLink
                external
                href={entity.wikipedia_url}
                event="topic_wikipedia_click"
                params={{ topic_slug: entity.slug }}
                className="underline"
              >
                Wikipedia
              </TrackedLink>{' '}
              (CC BY-SA)
            </p>
          </>
        )}
        <PeriodPills
          options={TOPIC_PERIODS.map((p) => ({
            id: p.id,
            label: p.label,
          }))}
          value={period}
          onChange={(id) => {
            setPeriod(id)
            setAllStories(false)
            setAllStorylines(false)
          }}
          className="mx-auto mt-5"
        />
      </header>

      <section
        aria-label={`Coverage of ${entity.name}`}
        className="mt-9 border-t border-foreground pt-6"
      >
        {articles === 0 ? (
          <p className="text-center font-sans text-[13px] text-muted-foreground">
            No stories in the last {periodDays} days.
          </p>
        ) : (
          <CoverageBlock
            counts={lean}
            articles={`Last ${periodDays} days · ${plural(inPeriod.length, 'story', 'stories')} · ${plural(articles, 'article', 'articles')}`}
            label={label}
          />
        )}
      </section>

      {/* A topic is ongoing: every day of the period, quiet ones too (Sam,
          2026-11-01), the same day-by-day chart a storyline page shows. */}
      {articles > 0 && (
        <section className="mt-[26px] sm:mt-[46px] sm:border-t sm:border-foreground sm:pt-[26px]">
          <h2 className="mb-1 font-serif text-2xl font-medium sm:text-[28px]">
            Who covered it, day by day
          </h2>
          <StorylineDayChart days={chartDays} from={from} to={today} />
        </section>
      )}

      <section className="mt-[46px] flex flex-wrap gap-11">
        <div className="min-w-0 flex-[999_1_560px]">
          <h2 className="mb-1.5 font-serif text-2xl font-medium sm:text-[28px]">
            {words.appears}
          </h2>
          {inPeriod.length === 0 ? (
            <p className="border-t border-border pt-4 font-sans text-sm text-muted-foreground">
              No stories in the last {periodDays} days.
            </p>
          ) : (
            shownStories.map((s) => (
              <StoryRow key={s.id} story={s} entity={entity} />
            ))
          )}
          {!allStories && inPeriod.length > STORIES_N && (
            <button
              type="button"
              onClick={() => {
                setAllStories(true)
                track('topic_page_show_all', {
                  topic_slug: entity.slug,
                  list: 'stories',
                })
              }}
              className={`mt-4 font-sans text-sm text-muted-foreground ${LINK}`}
            >
              Show all {inPeriod.length} stories
            </button>
          )}
        </div>

        <aside className="min-w-0 flex-[1_1_300px]">
          {periodStorylines.length > 0 && (
            <>
              <h2 className="dateline mb-3">Storylines</h2>
              <div className="mb-[30px]">
                {shownStorylines.map((s) => (
                  <TrackedLink
                    key={s.id}
                    href={`/storyline/${s.slug}`}
                    event="storyline_click"
                    params={{
                      source_surface: 'topic_page',
                      storyline_slug: s.slug,
                      topic_slug: entity.slug,
                    }}
                    className="block border-b border-border py-2.5 font-serif text-[19px] hover:underline"
                  >
                    {s.title}
                  </TrackedLink>
                ))}
                {!allStorylines && periodStorylines.length > STORYLINES_N && (
                  <button
                    type="button"
                    onClick={() => setAllStorylines(true)}
                    className={`mt-4 font-sans text-sm text-muted-foreground ${LINK}`}
                  >
                    Show all {periodStorylines.length} storylines
                  </button>
                )}
              </div>
            </>
          )}
          {anyOutlets && (
            <>
              <h2 className="dateline mb-3.5">{words.covers}</h2>
              {WINGS.filter((w) => bySide[w].length > 0).map((w) => (
                <div
                  key={w}
                  className={`mb-4 border-t-4 pt-2.5 last:mb-0 ${WING_BORDER[w]}`}
                >
                  <p
                    className={`mb-1.5 font-sans text-xs font-semibold uppercase leading-normal tracking-[0.12em] ${WING_TEXT[w]}`}
                  >
                    {SIDE_LABEL[w]}
                  </p>
                  <p className="font-sans text-sm leading-[1.8]">
                    {bySide[w].map((o) => (
                      <span key={o.slug} className="block">
                        <TrackedLink
                          href={`/sources/${o.slug}`}
                          event="source_click"
                          params={{
                            source_surface: 'topic_page',
                            source_slug: o.slug,
                            topic_slug: entity.slug,
                          }}
                          className="inline-flex items-center gap-2 hover:underline"
                        >
                          <OutletIcon outlet={o} size="sm" className="mx-0.5" />
                          {o.name}
                        </TrackedLink>{' '}
                        · {o.count}
                      </span>
                    ))}
                  </p>
                </div>
              ))}
            </>
          )}
        </aside>
      </section>

      {/* The newsletter ask, once, at the end (2026-11-01). Guests only. */}
      {isGuest && (
        <SubscribeCard
          location="topic_end"
          standalone
          className="mt-12"
          pitch="The people and places in the news, the day’s biggest stories, and how each side covered them. Free."
        />
      )}
    </article>
  )
}

// A story the topic is in: its date, its title, the overview's sentence
// that names the topic as a quote, and the row mark (LeanMark).
function StoryRow({ story, entity }) {
  const counts = story.wings || { left: 0, center: 0, right: 0 }
  return (
    <article className="border-b border-border py-[18px]">
      <p className="font-sans text-xs leading-normal text-muted-foreground">
        {story.lead}
      </p>
      <h3 className="mb-2 mt-1 font-serif text-[21px] font-medium leading-[1.25]">
        <TrackedLink
          href={story.href}
          event="story_click"
          params={{
            source_surface: 'topic_page',
            story_slug: story.slug,
            topic_slug: entity.slug,
          }}
          className="hover:underline"
        >
          {story.title}
        </TrackedLink>
      </h3>
      {story.sentence && (
        <p className="mb-2.5 border-l-2 border-foreground pl-3 font-serif text-[15px] italic leading-[1.5] text-secondary-foreground">
          {story.sentence}
        </p>
      )}
      {totalOf(counts) > 0 && (
        <LeanMark
          counts={counts}
          label={coverageLabel(counts, story.coverage_label)}
          className="w-[200px] max-w-full"
        />
      )}
    </article>
  )
}
